import { useEffect, useState } from "react";
import { MskScore } from "../../models/interfaces/msk-score";
import { User } from "../../models/interfaces/user";
import moment from "moment";
import { MovementHealthResponse } from "../../models/interfaces/reports/movement-health-response";
import NonLinearLineChart from "../charts/non-linear-line-chart";
import { Utils } from "../../utilities/utils";
import { useTranslation } from "react-i18next";
import { MskScoreVersion } from "../../models/enumerations/msk-score-versions";

const COMPONENT_CLASS = "c-user-profile-assessments";

// -----------------------------------------------------------------------------------------
// #region Intefaces
// -----------------------------------------------------------------------------------------

interface UserProfileShoulderMobilityProps {
    mskScore?: MskScore;
    mskScoreVersion: MskScoreVersion;
    user: User;
    allMskScores: MskScore[];
    setAllMskScores: Function;
    movementResponses?: MovementHealthResponse;

}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const UserProfileShoulderMobility: React.FC<UserProfileShoulderMobilityProps> = (props: UserProfileShoulderMobilityProps) => {
    const { mskScore, allMskScores, movementResponses, mskScoreVersion } = props;
    const [shoulderMobilityHistory, setShoulderMobilityHistory] = useState<{ x: string, y: number }[]>();
    const { t } = useTranslation();

    useEffect(() => {
        const getHistory = () => {
            if (allMskScores) {
                let tempShoulderMobilityHistory = []

                for (const mskScore of allMskScores) {
                    tempShoulderMobilityHistory.push({
                        x: moment(mskScore && mskScore.created?.seconds ? mskScore.created.seconds * 1000 : NaN).format("MMM DD, YY"),
                        y: mskScore.movementScore?.shoulderMobilityScore?.percentage ?? 0
                    })
                }
                setShoulderMobilityHistory(tempShoulderMobilityHistory);
            }
        }
        getHistory();
    }, [allMskScores])

    return (
        <div className="flex gap-5">
            <div className={`${COMPONENT_CLASS}__score-section-card gap-4 items-center`}>
                <div className="text-base font-normal">{t('movementFocus.shoulderMobility')}</div>

                <div className={`${COMPONENT_CLASS}__movement-group-score-card ${Utils.getGreenOrRedColor(mskScore?.movementScore?.shoulderMobilityScore?.percentage, 75)}`}>
                    <div className={`${COMPONENT_CLASS}__movement-group-score-card__main-text`}>{mskScore?.movementScore?.shoulderMobilityScore?.percentage}</div>
                    <div className={`${COMPONENT_CLASS}__movement-group-score-card__sub-text`}>{t('userProfile.assessments.goal75')}</div>
                </div>
                {mskScoreVersion ? // 0 gets ignored since 0 means movementresponses
                    <>
                        <div className="flex gap-4">
                            <div className="flex flex-col p-4 gap-1 bg-black-gray-5" style={{ borderRadius: "10px" }}>
                                <span className="text-10 font-bold uppercase">{t('reports.wellness.leftSide')}</span>
                                {Utils.getAssessmentBreakdownCell(t('reports.wellness.paperPass'), mskScore?.movementScore?.shoulderMobilityScore?.paperPassLeft)}
                                {Utils.getAssessmentBreakdownCell(t('reports.wellness.fingertipsTouch'), mskScore?.movementScore?.shoulderMobilityScore?.fingerTipsLeft)}
                            </div>
                            <div className="flex flex-col p-4 gap-1 bg-black-gray-5" style={{ borderRadius: "10px" }}>
                                <span className="text-10 font-bold uppercase">{t('reports.wellness.rightSide')}</span>
                                {Utils.getAssessmentBreakdownCell(t('reports.wellness.paperPass'), mskScore?.movementScore?.shoulderMobilityScore?.paperPassRight)}
                                {Utils.getAssessmentBreakdownCell(t('reports.wellness.fingertipsTouch'), mskScore?.movementScore?.shoulderMobilityScore?.fingerTipsRight)}
                            </div>
                        </div>
                        <div>
                            {Utils.getPainBadge(mskScore?.movementScore?.shoulderMobilityScore?.painSeverity!)}
                        </div>
                    </>
                    : movementResponses ?
                        <>
                            <div className="flex gap-4">
                                <div className="flex flex-col p-4 gap-1 bg-black-gray-5" style={{ borderRadius: "10px" }}>
                                    <span className="text-10 font-bold uppercase">{t('reports.wellness.leftSide')}</span>
                                    {Utils.getAssessmentBreakdownCell(t('reports.wellness.paperPass'), movementResponses.shoulderMobilityReachLeft.itemOne)}
                                    {Utils.getAssessmentBreakdownCell(t('reports.wellness.fingertipsTouch'), movementResponses.shoulderMobilityReachLeft.itemTwo)}
                                </div>
                                <div className="flex flex-col p-4 gap-1 bg-black-gray-5" style={{ borderRadius: "10px" }}>
                                    <span className="text-10 font-bold uppercase">{t('reports.wellness.rightSide')}</span>
                                    {Utils.getAssessmentBreakdownCell(t('reports.wellness.paperPass'), movementResponses.shoulderMobilityReachRight.itemOne)}
                                    {Utils.getAssessmentBreakdownCell(t('reports.wellness.fingertipsTouch'), movementResponses.shoulderMobilityReachRight.itemTwo)}
                                </div>
                            </div>
                            <div>
                                {Utils.getPainBadge(movementResponses.shoulderMobilityQPain)}
                            </div>
                        </>
                        :
                        <div></div>
                }

            </div>
            {shoulderMobilityHistory && (
                <div className={`${COMPONENT_CLASS}__score-section-card chart-wrapper grow`}>
                    <NonLinearLineChart
                        dataPoints={shoulderMobilityHistory.map(history => history.y)}
                        labels={shoulderMobilityHistory.map(history => history.x)}
                        title={t('userProfile.assessments.shoulderMobilityHistory')}
                        sectionDetails={{
                            sections: 2,
                            rangeOne: { start: 0, end: 74 },
                            rangeTwo: { start: 75, end: 100 },
                            backgroundColorOrder: { first: "#FFA998", second: "#8BE3CE" },
                            dataPointColorOrder: { first: "#E7514F", second: "#00DFBC" },
                            yAxisLabels: { first: t('userProfile.assessments.below'), second: t('userProfile.assessments.above') },
                            decimals: false
                        }}
                        toolTipLabel={true} />
                </div>
            )}
        </div>
    );
}

export default UserProfileShoulderMobility;
